import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FadeInWhenVisible, SectionHeader } from "./SectionWrapper";
import { PERSONAL } from "../data/portfolio";

const CONTACT_LINKS = [
  {
    icon: "✉",
    label: "Email",
    sub: PERSONAL.email,
    href: `mailto:${PERSONAL.email}`,
  },
  {
    icon: "in",
    label: "LinkedIn",
    sub: "linkedin.com/in/nshw",
    href: PERSONAL.linkedin,
  },
  {
    icon: "⌥",
    label: "GitHub",
    sub: "github.com/niteshshaw",
    href: PERSONAL.github,
  },
];

export default function Contact({ isDark }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

 const handleSubmit = async (e) => {
  e.preventDefault();

  if (!form.name || !form.email || !form.message) return;

  const payload = { ...form };

  try {
    setStatus("sending"); 

    const res = await fetch("https://api.codetoprod.dev/api/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      throw new Error("Failed to send");
    }

    setStatus("sent"); 
    setForm({ name: "", email: "", message: "" });

  } catch (err) {
    console.error("Contact form error:", err);
    setStatus("idle");
  }
};

  return (
    <section
      id="contact"
      className={`py-24 ${isDark ? "bg-navy-800" : "bg-gray-50"}`}
    >
      <div className="max-w-6xl mx-auto px-6">
        <SectionHeader tag="// 05. let's connect" heading="" isDark={isDark} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <FadeInWhenVisible delay={0.1}>
            <p
              className={`leading-relaxed mb-8 ${isDark ? "text-gray-300" : "text-gray-600"}`}
            >
              I'm always open to discussing senior engineering roles,
              interesting architectural challenges, or collaborative
              opportunities. Whether you're hiring or just want to talk
              distributed systems — reach out.
            </p>

            <div className="space-y-3 mb-8">
              {CONTACT_LINKS.map((link, i) => (
                <motion.a
                  key={link.label}
                  href={link.href}
                  target={link.href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15 + i * 0.08 }}
                  whileHover={{ x: 6 }}
                  className={`flex items-center gap-4 p-4 rounded-xl border transition-all duration-200 group ${
                    isDark
                      ? "bg-navy-600 border-navy-300/50 hover:border-gold/50"
                      : "bg-white border-gray-200 hover:border-gold-muted/50 shadow-sm"
                  }`}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 transition-colors"
                    style={{
                      background: "rgba(232,184,75,0.1)",
                      border: "1px solid rgba(232,184,75,0.2)",
                      color: "var(--gold)",
                    }}
                  >
                    {link.icon}
                  </div>
                  <div>
                    <div
                      className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-800"}`}
                    >
                      {link.label}
                    </div>
                    <div
                      className={`font-mono text-xs mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                    >
                      {link.sub}
                    </div>
                  </div>
                  <span
                    className={`ml-auto font-mono text-sm transition-colors ${isDark ? "text-gray-600 group-hover:text-gold" : "text-gray-300 group-hover:text-gold-muted"}`}
                  >
                    →
                  </span>
                </motion.a>
              ))}
            </div>

            <div
              className="inline-flex items-center gap-2 font-mono text-xs tracking-wide px-4 py-2.5 rounded-xl"
              style={{
                background: "rgba(30,160,80,0.1)",
                border: "1px solid rgba(30,160,80,0.25)",
                color: "#4ade80",
              }}
            >
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              Open to Senior Java / Fullstack roles · Bangalore & Remote
            </div>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.2}>
            <AnimatePresence mode="wait">
              {status === "sent" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`rounded-xl border p-10 text-center ${
                    isDark
                      ? "bg-navy-600 border-navy-300/50"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <div className="text-4xl mb-4">✅</div>
                  <h3
                    className={`font-sans font-black text-lg mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    Message sent!
                  </h3>
                  <p
                    className={`font-mono text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  >
                    I'll get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-6 font-mono text-xs underline cursor-pointer bg-transparent border-none"
                    style={{ color: "var(--gold)" }}
                  >
                    Send another message
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit}
                  className={`rounded-xl border p-7 space-y-5 ${
                    isDark
                      ? "bg-navy-600 border-navy-300/50"
                      : "bg-white border-gray-200 shadow-sm"
                  }`}
                >
                  {[
                    {
                      name: "name",
                      label: "// name",
                      placeholder: "Your full name",
                      type: "text",
                    },
                    {
                      name: "email",
                      label: "// email",
                      placeholder: "your@email.com",
                      type: "email",
                    },
                  ].map((field) => (
                    <div key={field.name}>
                      <label
                        className={`font-mono text-xs tracking-widest block mb-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                      >
                        {field.label}
                      </label>
                      <input
                        type={field.type}
                        name={field.name}
                        value={form[field.name]}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        required
                        className={`w-full px-4 py-3 rounded-lg border text-sm font-sans outline-none form-field transition-all ${
                          isDark
                            ? "bg-navy-700/60 border-navy-400/50 text-white placeholder-gray-600 focus:border-gold"
                            : "bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400 focus:border-gold-muted"
                        }`}
                      />
                    </div>
                  ))}

                  <div>
                    <label
                      className={`font-mono text-xs tracking-widest block mb-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                    >
                      // message
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell me about the role or project you have in mind..."
                      required
                      rows={5}
                      className={`w-full px-4 py-3 rounded-lg border text-sm font-sans outline-none form-field transition-all resize-vertical ${
                        isDark
                          ? "bg-navy-700/60 border-navy-400/50 text-white placeholder-gray-600 focus:border-gold"
                          : "bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400 focus:border-gold-muted"
                      }`}
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={status === "sending"}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-3.5 rounded-full font-sans font-bold text-sm tracking-wide border-none cursor-pointer transition-all"
                    style={{
                      background: "var(--gold)",
                      color: "#0a0e1a",
                      opacity: status === "sending" ? 0.7 : 1,
                    }}
                  >
                    {status === "sending" ? "Sending..." : "Send Message →"}
                  </motion.button>
                </motion.form>
              )}
            </AnimatePresence>
          </FadeInWhenVisible>
        </div>
      </div>
    </section>
  );
}
